import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Loader2, Plus, Trash2 } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { useT } from "@/i18n";
import { useNoIndex } from "@/lib/noIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Changes = ({ d, t }) => {
  if (!d) return null;
  if (d.first) return <p className="mt-3 text-sm text-[#6E685E]">{t("comp.first", "Primo controllo fatto: dal prossimo report vedrai cosa cambia.")}</p>;
  const items = [
    d.title && `${t("comp.title", "Titolo")}: «${d.title[0] ?? ""}» → «${d.title[1] ?? ""}»`,
    d.description && t("comp.descChanged", "Descrizione per Google cambiata"),
    d.new_pages?.length > 0 && `${t("comp.newPages", "Pagine nuove")}: ${d.new_pages.length}`,
    d.removed_pages > 0 && `${t("comp.removed", "Pagine tolte")}: ${d.removed_pages}`,
    d.new_keywords?.length > 0 && `${t("comp.newKw", "Nuove parole chiave")}: ${d.new_keywords.join(", ")}`,
  ].filter(Boolean);
  return (
    <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-[#3D3833]" data-testid="competitor-changes">
      {(items.length ? items : [t("comp.none", "Nessun cambiamento dall'ultimo controllo.")]).map((x) => <li key={x}>{x}</li>)}
    </ul>
  );
};

const Row = ({ c, t, onDelete }) => (
  <li className="rounded-2xl border border-[#121110]/12 bg-white p-5" data-testid={`competitor-${c.id}`}>
    <div className="flex items-start justify-between gap-3">
      <a href={c.url} target="_blank" rel="noreferrer" className="tap inline-flex items-center gap-2 break-all font-semibold hover:text-[#9A5326]">{c.url} <ExternalLink className="h-4 w-4 shrink-0" /></a>
      <button onClick={() => onDelete(c.id)} aria-label={t("comp.del", "Rimuovi")} className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-[#121110]/15 hover:border-[#C26D38] hover:text-[#C26D38]" data-testid={`competitor-delete-${c.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
    {c.last?.ok ? (
      <dl className="mt-3 grid gap-1 text-sm text-[#3D3833]">
        <div><dt className="inline font-semibold">{t("comp.title", "Titolo")}: </dt><dd className="inline">{c.last.title || "-"}</dd></div>
        <div><dt className="inline font-semibold">{t("comp.desc", "Descrizione per Google")}: </dt><dd className="inline">{c.last.description || t("comp.missing", "(assente)")}</dd></div>
        <div><dt className="inline font-semibold">{t("comp.kw", "Parole più usate")}: </dt><dd className="inline">{(c.last.keywords ?? []).slice(0, 12).join(", ")}</dd></div>
        <div><dt className="inline font-semibold">{t("comp.pages", "Pagine nella sitemap")}: </dt><dd className="inline">{c.last.page_count ?? 0}</dd></div>
      </dl>
    ) : <p className="mt-3 text-sm text-[#6E685E]">{c.last ? t("comp.down", "Sito non raggiungibile nell'ultimo controllo.") : t("comp.pending", "Verrà controllato nel prossimo report.")}</p>}
    <Changes d={c.diff} t={t} />
  </li>
);

export default function CompetitorsManage() {
  const t = useT();
  const { token } = useParams();
  const [list, setList] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  useNoIndex();
  const fail = (err) => toast.error(err?.response?.data?.detail ?? t("res.err", "Invio non riuscito, riprova tra qualche minuto."));
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/competitors/manage/${token}`);
      setList(Array.isArray(r.data?.competitors) ? r.data.competitors : []);
    } catch {
      setList(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/competitors/manage/${token}`, { url });
      setList(r.data?.competitors ?? []);
      setUrl("");
      toast.success(t("comp.added", "Sito aggiunto: sarà nel prossimo report."));
    } catch (err) { fail(err); } finally { setBusy(false); }
  };
  const del = async (id) => {
    try { setList((await axios.delete(`${API}/competitors/manage/${token}/${id}`)).data?.competitors ?? []); } catch (err) { fail(err); }
  };
  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#121110]" data-testid="competitors-page">
      <Nav />
      <main className="mx-auto max-w-3xl px-5 pb-28 pt-40 sm:px-8">
        <h1 className="font-display text-4xl font-bold tracking-tight sm:text-5xl">{t("comp.h", "Siti concorrenti")}</h1>
        <p className="mt-5 text-base leading-relaxed text-[#4A453F]">{t("comp.p", "Ogni lunedì controlliamo questi siti e ti mandiamo per email cosa è cambiato: titolo e descrizione per Google, pagine nuove o tolte, parole chiave.")}</p>
        {list === false && <p className="mt-10 text-[#9A5326]" data-testid="competitors-expired">{t("comp.expired", "Link scaduto o non valido: richiedine uno nuovo dall'area riservata.")}</p>}
        {list === null && <Loader2 className="mt-10 h-6 w-6 animate-spin" />}
        {Array.isArray(list) && (
          <>
            <form onSubmit={add} className="mt-10 flex flex-col gap-3 sm:flex-row" data-testid="competitor-form">
              <input value={url} onChange={(e) => setUrl(e.target.value)} required placeholder="https://www.esempio.it" className="h-12 flex-1 rounded-full border border-[#121110]/20 bg-white px-5 text-base outline-none focus:border-[#C26D38]" data-testid="competitor-url-input" />
              <button disabled={busy} className="btn-amber justify-center disabled:opacity-60" data-testid="competitor-add-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("comp.add", "Aggiungi")}</button>
            </form>
            <ul className="mt-8 space-y-4" data-testid="competitor-list">
              {list.map((c) => <Row key={c.id} c={c} t={t} onDelete={del} />)}
            </ul>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
